import { Component, OnInit } from '@angular/core';
import { McpSession } from '../../../models/mcp-session.model';
import { McpSessionService } from '../../../services/mcp-session.service';

@Component({
  selector: 'app-mcp-sessions-page',
  templateUrl: './mcp-sessions-page.component.html',
  styleUrl: './mcp-sessions-page.component.scss'
})
export class McpSessionsPageComponent implements OnInit {

  public first: number = 0;

  /* Filtra en vivo sobre la lista ya cargada: no hace falta ir al servidor por
     cada tecla. */
  public searchTerm: string = '';

  /* Filas fantasma con el mismo alto que las reales. NO se usa [loading] de
     p-table: ese pinta un overlay que bloquea. */
  public skeletonRows: null[] = [null, null, null, null, null];

  /* Solo un cierre a la vez; el spinner vive en el botón de esa fila. */
  public deletingMcpSessionId: string | null = null;

  /* Única región viva de la pantalla. Vive SIEMPRE en el DOM: un aria-live que
     aparece junto con su texto no se anuncia. */
  public liveMessage: string = '';

  /* El getter del filtro se evalúa en cada ciclo de detección. Devolver un
     array nuevo cada vez dispara NG0100 en desarrollo y hace que p-table
     reprocese su value sin necesidad; por eso se memoriza por (lista, término).
     El service siempre REEMPLAZA el array al cargar/cerrar, así que comparar la
     referencia basta para invalidar. */
  private filterCache: {
    source: McpSession[];
    term: string;
    result: McpSession[];
  } | null = null;

  constructor(
    public mcpSessionService: McpSessionService
  ) { }

  /* La lista se pide una sola vez, al entrar a la página. */
  async ngOnInit(): Promise<void> {

    this.liveMessage = 'Cargando sesiones MCP';

    await this.mcpSessionService.getAll();

    const total: number = this.mcpSessionService.mcpSessions.length;

    if (total === 0) {
      this.liveMessage = 'No hay sesiones MCP abiertas';
    } else {
      this.liveMessage = total === 1
        ? '1 sesión MCP abierta'
        : `${total} sesiones MCP abiertas`;
    }

  }

  get filteredMcpSessions(): McpSession[] {
    const source = this.mcpSessionService.mcpSessions;
    const words = McpSession.searchWords(this.searchTerm);

    if (words.length === 0) {
      return source;
    }

    const term = words.join(' ');

    if (
      this.filterCache &&
      this.filterCache.source === source &&
      this.filterCache.term === term
    ) {
      return this.filterCache.result;
    }

    const result = source.filter(mcpSession => {
      const text = mcpSession.searchText;
      return words.every(word => text.includes(word));
    });

    this.filterCache = { source, term, result };

    return result;
  }

  /* El valor de la tabla: esqueletos mientras carga, sesiones cuando llegan. */
  get tableValue(): (McpSession | null)[] {
    return this.mcpSessionService.loading
      ? this.skeletonRows
      : this.filteredMcpSessions;
  }

  /* Vacío por búsqueda y vacío por no haber sesiones son dos pantallas
     distintas: la primera ofrece limpiar, la segunda no tiene nada que ofrecer. */
  get isSearchEmpty(): boolean {
    return McpSession.searchWords(this.searchTerm).length > 0
      && this.mcpSessionService.mcpSessions.length > 0;
  }

  /*
   ██████╗██╗     ███████╗ █████╗ ██████╗  ███████╗███████╗ █████╗ ██████╗  ██████╗██╗  ██╗
  ██╔════╝██║     ██╔════╝██╔══██╗██╔══██╗ ██╔════╝██╔════╝██╔══██╗██╔══██╗██╔════╝██║  ██║
  ██║     ██║     █████╗  ███████║██████╔╝ ███████╗█████╗  ███████║██████╔╝██║     ███████║
  ██║     ██║     ██╔══╝  ██╔══██║██╔══██╗ ╚════██║██╔══╝  ██╔══██║██╔══██╗██║     ██╔══██║
  ╚██████╗███████╗███████╗██║  ██║██║  ██║ ███████║███████╗██║  ██║██║  ██║╚██████╗██║  ██║
   ╚═════╝╚══════╝╚══════╝╚═╝  ╚═╝╚═╝  ╚═╝ ╚══════╝╚══════╝╚═╝  ╚═╝╚═╝  ╚═╝ ╚═════╝╚═╝  ╚═╝

  */
  // region clearSearch
  clearSearch(): void {
    this.searchTerm = '';
    this.first = 0;
  }

  /*
  ██████╗ ███████╗██╗     ███████╗████████╗███████╗
  ██╔══██╗██╔════╝██║     ██╔════╝╚══██╔══╝██╔════╝
  ██║  ██║█████╗  ██║     █████╗     ██║   █████╗
  ██║  ██║██╔══╝  ██║     ██╔══╝     ██║   ██╔══╝
  ██████╔╝███████╗███████╗███████╗   ██║   ███████╗
  ╚═════╝ ╚══════╝╚══════╝╚══════╝   ╚═╝   ╚══════╝

  */
  // region delete
  async delete(
    mcpSession: McpSession
  ): Promise<void> {

    if (this.deletingMcpSessionId) {
      return;
    }

    const where = mcpSession.hasClient
      ? ` en ${mcpSession.clientLabel}`
      : '';

    const flag = confirm(
      `¿Está seguro que desea cerrar la sesión MCP de ${mcpSession.userFullName}${where}?\n\n` +
      'Tendrá que volver a iniciar sesión en el MCP para seguir usándolo.'
    );

    if (!flag) {
      return;
    }

    this.deletingMcpSessionId = mcpSession.mcpSessionId;

    const deleted = await this.mcpSessionService.delete(mcpSession);

    this.deletingMcpSessionId = null;

    if (deleted) {

      /* Si era la última fila de la página, se regresa a la anterior para no
         quedar viendo una página vacía. */
      if (this.first > 0 && this.first >= this.filteredMcpSessions.length) {
        this.first = Math.max(0, this.first - 10);
      }

      this.liveMessage = `Sesión MCP de ${mcpSession.userFullName} cerrada`;
    }

  }

}
